/**
 * useFocusTrap — the keyboard contract a modal dialog owes its user.
 *
 * A dialog that does not trap focus is a dialog only to sighted mouse users:
 * everyone else tabs straight out of it and carries on interacting with a page
 * they cannot see, behind a backdrop they cannot dismiss. AppShell's mobile
 * drawer already does this properly; this is the same behaviour, shared, so
 * every dialog gets it rather than each one reinventing a piece of it.
 *
 * What it provides while `active`:
 *   • focus moves into the dialog on open, to the first sensible control;
 *   • Tab and Shift+Tab cycle within it and never escape;
 *   • Escape calls `onClose`;
 *   • focus returns to whatever was focused before it opened.
 *
 * It deliberately does not render anything or own the open state: a dialog's
 * markup is its own business.
 */
import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) =>
      !element.hasAttribute('inert') &&
      // getClientRects rather than offsetParent: the latter is null for a
      // position:fixed descendant in some engines, which would silently drop a
      // fixed footer's buttons out of the cycle.
      element.getClientRects().length > 0 &&
      element.getAttribute('aria-hidden') !== 'true',
  );
}

export interface UseFocusTrapOptions {
  /** Closes on Escape. Omit to handle Escape yourself. */
  onClose?: () => void;
  /**
   * Focused on open. Without it the first focusable control is used — which is
   * often a close button, so a form should pass its first field.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  { onClose, initialFocusRef }: UseFocusTrapOptions = {},
) {
  // Read through a ref so a caller passing an inline arrow does not re-run the
  // effect on every render and steal focus back mid-interaction.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusFirst = () => {
      const target = initialFocusRef?.current ?? focusableWithin(container)[0] ?? container;
      target.focus();
    };
    // After paint, so the dialog's children exist to be focused.
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = focusableWithin(container);
      if (focusables.length === 0) {
        // Nothing to move to: keep focus on the dialog itself.
        event.preventDefault();
        container.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement;

      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (current === last || !container.contains(current))) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      // Only take focus back if it is still inside the dialog being torn down;
      // otherwise something else has deliberately claimed it.
      const activeNow = document.activeElement;
      if (previouslyFocused && (!activeNow || container.contains(activeNow))) {
        previouslyFocused.focus();
      }
    };
  }, [active, containerRef, initialFocusRef]);
}
